import {useCallback, useRef, useState} from 'react';
import {useSelector} from 'react-redux';

import {
    createIntegrationContract,
    createIntegrationContractSuccess,
    getContractsDetails,
    getIntegrationContracts,
    savePayPerCallContract,
    savePayPerCallContractSuccess,
    saveSubscriptionContract,
    saveSubscriptionContractSuccess,
} from 'src/openapi/admin/apis/ContractControllerApi';
import {IntegrationPartner} from 'src/openapi/admin/models/IntegrationPartner';
import {PackageSize} from 'src/openapi/admin/models/PackageSize';
import {PricingModel} from 'src/openapi/admin/models/PricingModel';
import {useAppDispatch} from 'src/store/hooks';

import {ConcurrentStrategies} from '@pb/middleware-api';

import {getAccountId} from '../../../../services/selectors';

import type {CreateContract, EditIntegrationContract, EditPPCContract, EditSubscriptionContract} from '../typedef';
import type {Error} from 'src/openapi/admin/models/Error';


export const useCreateContract = (onCreateContractCb?: (contractNumber: string) => void) => {
    const apiDispatch = useAppDispatch();
    const [error, setError] = useState<Error | null>(null);
    const [busy, setBusy] = useState(false);
    const ppcData = useRef<EditPPCContract>({
        costPerCredit: 0,
        initialPackage: 0,
        startDate: '',
    });

    const subscriptionData = useRef<EditSubscriptionContract>({
        costPerCredit: 0,
        initialPackage: 0,
        overageCost: 0,
        endDate: '',
        startDate: '',
        packageSize: PackageSize.CUSTOM,
    });

    const integrationData = useRef<EditIntegrationContract>({
        integrationPartner: IntegrationPartner.SANDBOX,
        startDate: '',
        endDate: '',
    });
    const [saveAvailable, setSaveAvailable] = useState(false);
    const accountId: number = useSelector(getAccountId);

    const onCreateContractSuccess = useCallback((contractNumber: string) => {
        if (onCreateContractCb) {
            onCreateContractCb(contractNumber);
        }
    }, [onCreateContractCb]);

    const onCreateSubscriptionContract = useCallback(async (data: CreateContract) => {
        const response = await apiDispatch(saveSubscriptionContract({
            accountId,
            subscriptionContractSave: {
                ...data,
                ...subscriptionData.current,
            },
        }));

        if (response.type === saveSubscriptionContractSuccess.type) {
            await apiDispatch(getContractsDetails({accountId}, {
                concurrentStrategy: ConcurrentStrategies.LAST,
            }));
            await onCreateContractSuccess(data.contractNumber);
        } else if (response?.payload?.error) {
            setError(response?.payload?.data);
        }
    }, [accountId, onCreateContractSuccess, apiDispatch]);

    const onCreatePayPerCallContract = useCallback(async (data: CreateContract) => {
        const response = await apiDispatch(savePayPerCallContract({
            accountId,
            payPerCallContractSave: {
                ...data,
                ...ppcData.current,
            },
        }));

        if (response.type === savePayPerCallContractSuccess.type) {
            await apiDispatch(getContractsDetails({accountId}, {
                concurrentStrategy: ConcurrentStrategies.LAST,
            }));
            await onCreateContractSuccess(data.contractNumber);
        } else if (response.payload.error) {
            setError(response?.payload?.data);
        }
    }, [accountId, onCreateContractSuccess, apiDispatch]);

    const onCreateIntegrationContract = useCallback(async (data: CreateContract) => {
        const response = await apiDispatch(createIntegrationContract({
            accountId,
            integrationContractCreate: {
                ...data,
                ...integrationData.current,
            },
        }));

        if (response.type === createIntegrationContractSuccess.type) {
            await apiDispatch(getIntegrationContracts({accountId}, {
                concurrentStrategy: ConcurrentStrategies.LAST,
            }));
            await onCreateContractSuccess(data.contractNumber);
        } else if (response?.payload?.error) {
            setError(response?.payload?.data);
        }
    }, [accountId, onCreateContractSuccess, apiDispatch]);

    const onCreateContract = async (pricingModel: PricingModel, data: CreateContract) => {
        setBusy(true);
        switch (pricingModel) {
            case PricingModel.SUBSCRIPTION:
                await onCreateSubscriptionContract(data);
                break;
            case PricingModel.PAY_PER_CALL:
                await onCreatePayPerCallContract(data);
                break;
            case PricingModel.INTEGRATION:
                await onCreateIntegrationContract(data);
                break;
        }
        setBusy(false);
    };

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    const updateData = useCallback((data: EditIntegrationContract | EditPPCContract) => {
        if (error) {
            clearError();
        }
        const checkedData = typeof data === 'object' && data !== null ? data : {};

        setSaveAvailable(Object.values(checkedData).every(val => val));
    }, [error, clearError]);

    const onEditIntegrationData = useCallback((inputsData: EditIntegrationContract) => {
        updateData(inputsData);
        integrationData.current = inputsData;
    }, [updateData]);

    const onEditPPCData = useCallback((inputsData: EditPPCContract) => {
        updateData(inputsData);
        ppcData.current = inputsData;
    }, [updateData]);

    const onEditSubscriptionData = useCallback((inputsData: EditSubscriptionContract) => {
        updateData(inputsData);
        subscriptionData.current = inputsData;
    }, [updateData]);

    return ({
        busy,
        error,
        clearError,
        saveAvailable,
        onCreateContract,
        onEditSubscriptionData,
        onEditPPCData,
        onEditIntegrationData,
    });
};
